/**
 * @file page-h1.ts
 * @description Главный заголовок (H1) на странице (phase19.16 Р2;
 * uiux-design-r2 §10 «Второй H1»; SB92): где ещё на странице есть H1 —
 * в других секциях или в этой же (шапка раздела и элементы). Неподдерживаемые
 * блоки не считаются.
 *
 * Р2а (uiux-design-r2a §6): в шаблоне страницы товара главный заголовок —
 * элемент «Название» (всегда H1); заголовок H1 рядом с ним получает своё
 * предупреждение «Название товара уже главный заголовок — сделайте этот H2».
 *
 * Р3в (v0.19.64): подсчёт H1 секции — общий с api и «Проверкой сайта»
 * (`sectionHasOtherH1`, `sectionHasProductTitle` из `@chaiberi/shared`
 * site-seo); здесь — только поиск блока для текста предупреждения.
 */

import { sectionHasOtherH1, sectionHasProductTitle, type H1Subject, type SectionPayload } from "@chaiberi/shared";

import { blockTitle, isSectionBlock } from "../../components/site-pages/block-meta";
import type { SitePageBlock } from "../api-site-pages";

/** Что сейчас правится: шапка раздела или элемент с id (тип — из shared, Р3в). */
export type { H1Subject };

/** Где уже есть главный заголовок: в блоке (по названию) или это «Название» товара (шаблон, Р2а). */
export type OtherH1 = { kind: "block"; title: string } | { kind: "product-title" };

/**
 * Где уже есть H1 (для текста предупреждения); `null` — нет.
 * `current` — правимая секция с несохранённым payload.
 */
export function otherH1Block(
  blocks: readonly SitePageBlock[],
  current: { id: string; payload: SectionPayload; title: string },
  subject: H1Subject,
): OtherH1 | null {
  const found = (payload: SectionPayload, title: string): OtherH1 => (sectionHasProductTitle(payload) ? { kind: "product-title" } : { kind: "block", title });
  if (sectionHasOtherH1(current.payload, subject)) return found(current.payload, current.title);
  for (const block of blocks) {
    if (block.id === current.id || !isSectionBlock(block) || !block.isEnabled) continue;
    const payload = block.payload as SectionPayload;
    if (payload.columns && sectionHasOtherH1(payload, null)) return found(payload, blockTitle(block));
  }
  return null;
}

/** Текст предупреждения о втором H1 (uiux-design-r2 §10; шаблон товара — uiux-design-r2a §6). */
export function secondH1Text(other: OtherH1): string {
  if (other.kind === "product-title") return "Название товара уже главный заголовок страницы — сделайте этот заголовок H2.";
  return `На странице уже есть главный заголовок (блок «${other.title}»). Второй мешает поиску — сделайте его заголовком раздела.`;
}

/**
 * Тексты главных заголовков H1 страницы (Р3в, SEO12: проверка «главного
 * запроса нет в заголовке для поиска или в H1»): шапки разделов уровня 1 и
 * элементы «Заголовок» H1 включённых секций по порядку.
 */
export function pageH1Texts(blocks: readonly SitePageBlock[]): string[] {
  const texts: string[] = [];
  for (const block of blocks) {
    if (!isSectionBlock(block) || !block.isEnabled) continue;
    const payload = block.payload as SectionPayload;
    if (payload.header?.level === 1 && payload.header.title) texts.push(payload.header.title);
    for (const column of payload.columns?.items ?? []) {
      for (const el of column.elements ?? []) {
        if (el.kind === "heading" && el.level === 1 && el.text) texts.push(el.text);
      }
    }
  }
  return texts;
}
